import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createMcpToken, type McpToken, type McpTokenFailure } from '../api.js';
import { mcpBaseUrl } from '../config.js';
import { signInPathFor } from '../return-path.js';

/** What to tell the user for each way a mint can fail — and whether re-authenticating is the fix. */
const FAILURE_MESSAGES: Record<McpTokenFailure, string> = {
  unauthenticated: 'Your session has expired. Sign in again to generate a token.',
  forbidden: 'This account does not have MCP access. An owner must grant it.',
  rate_limited: 'Too many token requests. Wait a minute and try again.',
  unavailable: 'Token minting is temporarily unavailable. Try again shortly.',
  server_error: 'Could not mint a token. Check your connection and try again.',
};

/**
 * Header affordance that mints a long-lived MCP token (ADR-0030) and shows it to copy, alongside the
 * ready-to-paste `claude mcp add` command. Uses the SPA's credentialed api client (the same path that
 * loads projects/session), so it works where a raw console fetch is blocked by an extension/CSP.
 *
 * Interim surface: superseded by Account Settings → API Tokens (persistent, revocable, masked).
 */
export function McpTokenPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<McpToken | null>(null);
  const [failure, setFailure] = useState<McpTokenFailure | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  async function generate() {
    setLoading(true);
    setFailure(null);
    setToken(null);
    const result = await createMcpToken();
    setLoading(false);
    if (!result.ok) {
      setFailure(result.reason);
      return;
    }
    setToken(result.token);
  }

  function openPanel() {
    setOpen(true);
    if (!token && !loading) void generate();
  }

  const connectCmd = token
    ? `claude mcp add --transport http ggpathos ${mcpBaseUrl}/mcp --header "Authorization: Bearer ${token.token}"`
    : '';

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard blocked (permissions) — the field is selectable, so the user can copy manually.
    }
  }

  return (
    <>
      <button type="button" className="app__navlink" onClick={openPanel}>
        MCP access
      </button>
      {open && (
        <div
          className="mcp__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="MCP access token"
          onClick={() => setOpen(false)}
        >
          <div className="mcp__panel" onClick={(e) => e.stopPropagation()}>
            <div className="mcp__head">
              <h2 className="mcp__title">MCP access token</h2>
              <button
                type="button"
                className="mcp__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="mcp__lead">
              A 24-hour token to connect an MCP client (e.g. Claude Code) to GGPathOS so you can ask
              about your portfolio in plain language. Treat it like a password.
            </p>

            {loading && <p className="mcp__muted">Generating…</p>}
            {failure && (
              <p className="mcp__error">
                {FAILURE_MESSAGES[failure]}{' '}
                {failure === 'unauthenticated' ? (
                  <button
                    type="button"
                    className="mcp__link"
                    onClick={() =>
                      void navigate(signInPathFor(window.location, window.location.origin))
                    }
                  >
                    Sign in
                  </button>
                ) : (
                  <button type="button" className="mcp__link" onClick={() => void generate()}>
                    Retry
                  </button>
                )}
              </p>
            )}

            {token && (
              <>
                <label className="mcp__label">
                  Token{' '}
                  <span className="mcp__muted">
                    · expires {new Date(token.expiresAt).toLocaleDateString()}
                  </span>
                </label>
                <div className="mcp__row">
                  <input
                    className="mcp__field"
                    readOnly
                    value={token.token}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    className="mcp__copy"
                    onClick={() => void copy(token.token, 'token')}
                  >
                    {copied === 'token' ? 'Copied' : 'Copy'}
                  </button>
                </div>

                <label className="mcp__label">Connect command</label>
                <div className="mcp__row">
                  <textarea
                    className="mcp__field mcp__field--cmd"
                    readOnly
                    value={connectCmd}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    className="mcp__copy"
                    onClick={() => void copy(connectCmd, 'cmd')}
                  >
                    {copied === 'cmd' ? 'Copied' : 'Copy'}
                  </button>
                </div>

                <p className="mcp__muted">
                  Then ask, e.g. “List my GGPathOS projects and their verdicts.”
                </p>
                <button type="button" className="mcp__regen" onClick={() => void generate()}>
                  Regenerate
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
